import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Info, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { generateInviteCode, getInviteCode } from '@/entities/workspace/api';
import { UpgradePromotionCard } from '../../saved-queries/ui/upgrade-promotion-card';
import { MemberRow } from '../ui/member-row';
import { SettingPageHeader } from '../ui/setting.header';
import { SettingSectionLayout } from '../ui/setting.section';

import { useUser } from '@/entities/auth/hooks/use-user';
import { changeMemberRole, fetchMembers } from '@/entities/member/api';
import type { Member } from '@/entities/member/types';
import { USER_ROLE } from '@/entities/user/constants';
import { WORKSPACE_PLAN } from '@/entities/workspace/constants';
import { Button } from '@/shared/components/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { Stage } from '@/shared/components/stage/stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

export function MemberPage() {
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const navigate = useNavigate();
  return (
    <SettingSectionLayout>
      <Stage>
        <Stage.Scene
          status={
            (workspace?.plan === WORKSPACE_PLAN.DEMO ||
              workspace?.plan === WORKSPACE_PLAN.ENTERPRISE) &&
            workspace?.privilege !== USER_ROLE.VIEWER
          }
        >
          <SettingPageHeader title="People" />
          <InviteLinkSection />
          <MembersSection />
        </Stage.Scene>
        <Stage.Scene default>
          <UpgradePromotionCard
            title="Upgrade to Enterprise plan"
            description="You can invite team members by upgrading to the Enterprise plan."
            actions={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    navigate({
                      to: '/workspace/$workspaceId/settings/plan',
                      params: { workspaceId },
                    });
                  }}
                >
                  View plans
                  <ArrowRight className="size-4" />
                </Button>
              </>
            }
          />
          <div className="p-3"></div>
          <SettingPageHeader title="People" />
          <MembersSection />
        </Stage.Scene>
      </Stage>
    </SettingSectionLayout>
  );
}

const LINK_TYPES: USER_ROLE[] = [USER_ROLE.VIEWER, USER_ROLE.MANAGER];

function InviteLinkSection() {
  const { workspaceId } = useWorkspaceId();

  const [linkType, setLinkType] = useState<USER_ROLE>(USER_ROLE.VIEWER);
  const [copied, setCopied] = useState(false);

  const {
    data: inviteCode,
    refetch: refetchInviteCode,
    isFetching,
  } = useQuery({
    queryKey: ['inviteCode', linkType, workspaceId],
    queryFn: () => getInviteCode(workspaceId, linkType),
  });

  const handleLinkTypeChange = (value: USER_ROLE) => {
    setLinkType(value);
  };

  const inviteLink = `${window.location.origin}/invite?token=${inviteCode?.code}`;

  const handleCopy = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteLink);
    toast.success('Copied to clipboard');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleGenerateNewLink = async () => {
    await generateInviteCode(workspaceId, linkType);
    refetchInviteCode();
  };

  return (
    <div className="mb-8 flex w-full flex-col gap-2">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Invite link</span>
          <TooltipFlat
            asChild
            content={
              <p>
                Invite link is a link that allows users to join the workspace.
                <br />
                And it will expire in 7 days.
              </p>
            }
          >
            <Info className="size-4" />
          </TooltipFlat>
        </div>
        {inviteCode && (
          <button
            type="button"
            className="text-foreground text-sm underline"
            onClick={handleGenerateNewLink}
          >
            Reset link
          </button>
        )}
      </div>
      <div className="flex w-full gap-2">
        <div className="bg-background flex h-10 flex-1 items-center rounded-md border px-3">
          <Stage>
            <Stage.Scene status={isFetching}>
              <div className="flex h-10 w-full items-center justify-center">
                <Loader2 className="size-4 animate-spin" />
              </div>
            </Stage.Scene>
            <Stage.Scene status={!!inviteCode}>
              <span className="text-foreground truncate text-sm">{inviteLink}</span>
            </Stage.Scene>
            <Stage.Scene default status={!inviteCode}>
              <button
                type="button"
                className="text-foreground text-sm underline"
                onClick={handleGenerateNewLink}
              >
                Generate new link
              </button>
            </Stage.Scene>
          </Stage>
        </div>
        <Select value={linkType} onValueChange={handleLinkTypeChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Select a role" />
          </SelectTrigger>
          <SelectContent>
            {LINK_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                As a {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="secondary"
          size="default"
          className="h-10 rounded-md border px-4 py-2"
          onClick={handleCopy}
          disabled={!inviteCode}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </Button>
      </div>
    </div>
  );
}

function MembersSection() {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const { user } = useUser();

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => fetchMembers(workspaceId),
  });

  const mutateMember = useMutation({
    mutationFn: (member: Member) => changeMemberRole(workspaceId, member.id, member.privilege),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', workspaceId] });
    },
  });

  const getEditable = (isMe: boolean) => {
    if (isMe) return false;
    if (workspace?.privilege === USER_ROLE.MANAGER || workspace?.privilege === USER_ROLE.OWNER) {
      return true;
    }
    return false;
  };

  return (
    <div className="w-full">
      <div className="mb-2 text-sm font-medium">Members</div>
      <Table className="table-fixed">
        <TableHeader>
          <TableRow gridTemplateColumns="1fr 200px">
            <TableHead className="w-full px-4!">Name</TableHead>
            <TableHead className="w-[120px] px-4!">Permission</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members?.map((member) => {
            const isMe = member.id === user?.id;
            const editable = getEditable(isMe);

            return (
              <MemberRow
                key={member.id}
                member={member}
                onPermissionChange={(member) => {
                  mutateMember.mutate(member);
                }}
                gridTemplateColumns="1fr 200px"
                editable={editable}
              />
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
